import { ChangeEvent, useId } from 'react'

import {
  FieldContainer,
  FieldLabel,
  InputRow,
  Prefix,
  NumberInputElement,
  HelperText,
} from './styled'
import { NumberFieldProps } from './types'

/**
 * Numeric input with optional prefix (e.g. "$"). Empty value reports as
 * `null` rather than zero so the renter can leave a price unset.
 *
 * @param {NumberFieldProps} props
 * @returns {JSX.Element}
 */
const NumberField = ({
  label,
  value,
  onChange,
  prefix,
  placeholder,
  errorMessage,
  helperText,
  step,
  min,
  max,
}: NumberFieldProps): JSX.Element => {
  const inputId = useId()
  const hasError = errorMessage !== undefined && errorMessage !== ''
  const supplementaryText =
    hasError === true ? errorMessage : (helperText ?? '')

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const raw = event.target.value

    if (raw === '') {
      onChange(null)
      return
    }

    const parsed = Number(raw)

    if (Number.isNaN(parsed)) {
      return
    }

    onChange(parsed)
  }

  return (
    <FieldContainer htmlFor={inputId}>
      <FieldLabel>{label}</FieldLabel>
      <InputRow hasError={hasError}>
        {prefix !== undefined ? <Prefix>{prefix}</Prefix> : null}
        <NumberInputElement
          id={inputId}
          type="number"
          value={value === null ? '' : value}
          onChange={handleChange}
          placeholder={placeholder}
          step={step}
          min={min}
          max={max}
          inputMode="numeric"
        />
      </InputRow>
      {supplementaryText !== '' ? (
        <HelperText isError={hasError}>{supplementaryText}</HelperText>
      ) : null}
    </FieldContainer>
  )
}

export default NumberField
